import { useState } from "react"
import { Karte, Knopf, Leer } from "@/components/ui"
import { gruppiere, jahrLabel, kennwert, stat, type AuswertungsDaten, type Filter, type Stat } from "@/lib/auswertung"
import { mmss, nf0, nf1 } from "@/lib/format"

interface Props {
  d: AuswertungsDaten
  tp: Float64Array
  ids: number[]
  f: Filter
}

/** Wie die Pivot-Tabellen der Excel-Auswertung: NC-Typ, darunter Gebinde · Medium · Anfeuchtung Soll. */
export function Fahrweisen({ d, tp, ids, f }: Props) {
  const [offen, setOffen] = useState<Set<number>>(new Set())
  const jahre = [...f.jahre].sort((a, b) => a - b)
  const proTyp = [...gruppiere(ids, (i) => d.rows.t[i])].sort((a, b) => b[1].length - a[1].length)
  const alleOffen = proTyp.length > 0 && proTyp.every(([t]) => offen.has(t))
  const td = "border-b border-grid px-2 py-1.5 text-right whitespace-nowrap"
  const th = "sticky top-0 bg-surface px-2 py-1.5 text-right text-[11.5px] font-medium text-muted whitespace-nowrap"

  const proJahr = (g: number[]) => {
    const pj = gruppiere(g, (i) => d.rows.y[i])
    return jahre.map((y) => {
      const gg = pj.get(y) ?? []
      return { n: gg.length, v: gg.length ? kennwert(gg.map((i) => tp[i]), f.kw) : null }
    })
  }
  const zeile = (key: string, erste: React.ReactNode, s: Stat, pj: Array<{ n: number; v: number | null }>, cls: string) => (
    <tr key={key} className={cls}>
      <td className="border-b border-grid px-2 py-1.5 text-left">{erste}</td>
      <td className={td}>{nf0.format(s.n)}</td>
      <td className={td}>{mmss(s.z ?? 0)}</td>
      <td className={td}>{s.f == null ? "–" : nf1.format(s.f)}</td>
      <td className={td}>{nf1.format(s.aw ?? 0)}</td>
      {pj.map((c, i) => (
        <td key={i} title={`${c.n} Proben`} className={`${td} ${c.v == null || c.n < 3 ? "text-muted" : ""}`}>
          {c.v == null ? "–" : nf0.format(c.v)}
        </td>
      ))}
      <td className={td}>{nf0.format(s.tp ?? 0)}</td>
    </tr>
  )

  return (
    <Karte
      titel="Fahrweisen im Detail"
      unter="Entspricht den Pivot-Tabellen der Excel-Auswertung: NC-Typ, darunter Gebinde · Medium · Anfeuchtung Soll. Zykluszeit, Füll- und Auswurfgewicht über alle gewählten Jahre."
      rechts={
        <Knopf variante="link" onClick={() => setOffen(alleOffen ? new Set() : new Set(proTyp.map(([t]) => t)))}>
          {alleOffen ? "Alle zuklappen" : "Alle aufklappen"}
        </Knopf>
      }
    >
      {!ids.length ? <Leer /> : (
        <div className="min-w-0 overflow-x-auto">
          <table className="tabular w-full border-collapse text-[12.5px]">
            <thead>
              <tr>
                <th className={`${th} text-left`}>NC-Typ / Fahrweise</th>
                <th className={th}>Proben</th>
                <th className={th}>Zykluszeit</th>
                <th className={th}>Füllgew. kg</th>
                <th className={th}>Auswurf kg</th>
                {jahre.map((y) => <th key={y} className={th}>{`${jahrLabel(d, y)} kg/h`}</th>)}
                <th className={th}>Gesamt kg/h</th>
              </tr>
            </thead>
            <tbody>
              {proTyp.flatMap(([t, g]) => {
                const istOffen = offen.has(t) || f.typ === t
                const fw = [...gruppiere(g, (i) => `${d.rows.g[i]}|${d.rows.md[i]}|${d.rows.a[i]}`)].sort((a, b) => b[1].length - a[1].length)
                const knopf = (
                  <button
                    type="button"
                    aria-expanded={istOffen}
                    onClick={() => {
                      const neu = new Set(offen)
                      if (neu.has(t)) neu.delete(t)
                      else neu.add(t)
                      setOffen(neu)
                    }}
                    className="inline-flex items-center gap-1.5 font-mono font-medium"
                  >
                    <span aria-hidden="true" className={`inline-block w-2.5 text-muted transition-transform ${istOffen ? "rotate-90" : ""}`}>▸</span>
                    {d.dims.typ[t]}
                    <span className="font-sans font-normal text-muted">{` · ${fw.length} Fahrweise${fw.length > 1 ? "n" : ""}`}</span>
                  </button>
                )
                const kopf = zeile(`t${t}`, knopf, stat(d, tp, g, f.kw), proJahr(g), `font-medium ${f.typ === t ? "bg-accent/10" : ""}`)
                if (!istOffen) return [kopf]
                return [kopf, ...fw.map(([k, gg]) => {
                  const [gi, mi, ai] = k.split("|").map(Number)
                  const anf = d.dims.anf[ai] === "ohne" ? "ohne Anfeuchtung" : `${d.dims.anf[ai]} %`
                  return zeile(`t${t}-${k}`, <span className="pl-5">{`${d.dims.geb[gi]} · ${d.dims.med[mi]} · ${anf}`}</span>,
                    stat(d, tp, gg, f.kw), proJahr(gg), "bg-surface2 text-ink2")
                })]
              })}
            </tbody>
          </table>
        </div>
      )}
    </Karte>
  )
}
